import React, { useEffect, useState, useRef } from "react";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { MessageBox, Input } from "react-chat-elements";
import {
  PlusCircleIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  PaperClipIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import { FaDownload } from "react-icons/fa";
import Logo from "assets/img/logoJaune.png";
import FileSubmission from "../../../common/FileSubmission";
import { BASE_URL } from "server";

// Types locaux pour éviter les dépendances externes
interface ChatFile {
  _id: string;
  filename: string;
  originalName: string;
  encoding: string;
  mimeType: string;
  destination: string;
  path: string;
  size: number;
  createdAt: string;
  __v?: number;
}

interface Message {
  id: string;
  title: string;
  position: "left" | "right";
  type: "text" | "files";
  text: string;
  date: Date;
  data?: {
    uri?: string;
    status?: {
      click: boolean;
      loading: number;
    };
    name?: string;
    extension?: string;
    size?: string;
    files?: ChatFile[];
  };
}

type ChatContainerProps = {
  conversationId: string;
  conversationTitle: string;
  onBack: () => void;
  userRole?: string;
};

// Fonction pour créer le titre de la conversation
const createTitle = (title: string) => (
  <div className="flex items-center space-x-2">
    <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
      <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
    </div>
    <div className="flex flex-col">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-xs text-gray-500">Support</p>
    </div>
  </div>
);

// Fonction pour télécharger un fichier
async function downloadFileById(fileId: string, filename: string) {
  try {
    const response = await fetch(`${BASE_URL}/files/${fileId}/download`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const urlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = urlBlob;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlBlob);
  } catch (error) {
    console.error("Erreur lors du téléchargement du fichier:", error);
  }
}

// Fonction pour récupérer les messages
async function getMessages(
  conversationId: string,
  currentUserId?: string,
): Promise<Message[]> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/messages`,
      {
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des messages");
    }
    const data = await response.json();

    if (!data?.messages || !Array.isArray(data.messages)) {
      return [];
    }

    return data.messages.map((msg: any) => {
      // Déterminer si le message est de l'utilisateur connecté
      const isCurrentUser = msg.sender._id === currentUserId;

      // Déterminer le titre selon l'utilisateur
      let title: string;
      if (isCurrentUser) {
        title = "Vous";
      } else {
        // Pour les autres utilisateurs, afficher le rôle
        const roleLabels: Record<string, string> = {
          student: "Élève",
          teacher: "Enseignant",
          parent: "Parent",
          admin: "Administrateur",
          adminStartup: "Administrateur",
          adminSchool: "Admin École",
        };
        title = roleLabels[msg.sender.userRole] || msg.sender.userRole;
      }

      const message: Message = {
        id: msg._id,
        position: isCurrentUser ? "right" : "left",
        type: msg.type,
        text: msg.body || "",
        date: new Date(msg.timestamp),
        title: title,
        data: {
          status: { click: false, loading: 0 },
          files:
            msg.files?.map((file: any) => ({
              _id: file._id,
              filename: file.filename,
              originalName: file.originalName,
              encoding: file.encoding,
              mimeType: file.mimeType,
              destination: file.destination,
              path: file.path,
              size: file.size,
              createdAt: file.createdAt,
            })) || [],
        },
      };
      return message;
    });
  } catch (error) {
    console.error("Erreur lors de la récupération des messages:", error);
    return [];
  }
}

// Fonction pour envoyer un message texte
async function sendTextMessage(
  conversationId: string,
  body: string,
): Promise<void> {
  const url = `${BASE_URL}/conversations/${conversationId}/messages/add`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ body }),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'envoi du message");
  }
}

// Fonction pour obtenir l'icône selon le type de fichier
function getFileIcon(mimeType: string, fileName: string) {
  const extension = fileName.split(".").pop()?.toLowerCase();

  // Images
  if (
    mimeType.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(
      extension || "",
    )
  ) {
    return <PhotoIcon className="w-6 h-6 text-blue-500" />;
  }

  // Vidéos
  if (
    mimeType.startsWith("video/") ||
    ["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension || "")
  ) {
    return <FilmIcon className="w-6 h-6 text-purple-500" />;
  }

  // Audio
  if (
    mimeType.startsWith("audio/") ||
    ["mp3", "wav", "flac", "aac", "ogg"].includes(extension || "")
  ) {
    return <MusicalNoteIcon className="w-6 h-6 text-green-500" />;
  }

  // Archives
  if (
    mimeType.includes("zip") ||
    mimeType.includes("rar") ||
    mimeType.includes("7z") ||
    ["zip", "rar", "7z", "tar", "gz"].includes(extension || "")
  ) {
    return <ArchiveBoxIcon className="w-6 h-6 text-orange-500" />;
  }

  // Documents (par défaut)
  return <DocumentIcon className="w-6 h-6 text-red-500" />;
}

// Fonction pour formater la taille du fichier
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Fonction pour créer une boîte de message
function createMessageBox(message: Message, index: number) {
  if (!message) return null;

  try {
    const baseProps = {
      key: index,
      position: message.position,
      type: message.type === "files" ? "file" : "text",
      text: message.text || "",
      date: message.date,
      id: message.id,
      title: message.title || "",
      avatar: undefined,
      focus: false,
      titleColor: "black",
      forwarded: false,
      removeButton: false,
      status: "sent" as const,
      notch: false,
      retracted: false,
      onForwardClick: () => console.log("forward clicked!"),
      onReplyClick: () => console.log("reply clicked!"),
      onTitleClick: () => console.log("title clicked!"),
    };

    if (
      message.type === "files" &&
      message.data?.files?.length &&
      message.data.files.length > 0
    ) {
      return message.data.files.map((file: ChatFile, fileIndex: number) => (
        <div
          key={`${index}-${fileIndex}`}
          className={`flex ${
            message.position === "right" ? "justify-end" : "justify-start"
          } mb-2`}
        >
          <div
            className={`inline-flex items-center space-x-2 p-3 rounded-xl border max-w-xs shadow-sm ${
              message.position === "right"
                ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 border-blue-200"
                : "bg-gradient-to-r from-gray-50 to-slate-50 text-gray-800 border-gray-200"
            }`}
          >
            {/* Icône du fichier */}
            <div className="flex-shrink-0">
              {getFileIcon(file.mimeType, file.originalName)}
            </div>

            {/* Informations du fichier */}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate text-gray-900">
                {file.originalName}
              </div>
              <div className="text-xs text-gray-500">
                {formatFileSize(file.size)}
              </div>
            </div>

            {/* Bouton de téléchargement */}
            <button
              onClick={async () => {
                if (file._id) {
                  await downloadFileById(file._id, file.originalName);
                } else {
                  const link = document.createElement("a");
                  link.href = file.path;
                  link.download = file.originalName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 hover:scale-105 ${
                message.position === "right"
                  ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              title={`Télécharger ${file.originalName}`}
            >
              <FaDownload className="w-3 h-3" />
            </button>
          </div>
        </div>
      ));
    }

    return <MessageBox {...baseProps} type="text" replyButton={true} />;
  } catch (error) {
    console.error("Erreur lors de la création du message:", error);
    return null;
  }
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  conversationId,
  conversationTitle,
  onBack,
  userRole = "student",
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState<string>("");
  const [isFileSubmissionOpen, setIsFileSubmissionOpen] =
    useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<{
    loading: boolean;
    success: boolean;
    error: string | null;
    progress: number;
  }>({
    loading: false,
    success: false,
    error: null,
    progress: 0,
  });
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const [hasNewMessages, setHasNewMessages] = useState<boolean>(false);
  const authHeader = useAuthHeader();
  const currentUser = useAuthUser<any>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousMessagesLengthRef = useRef<number>(0);
  const shouldAutoScrollRef = useRef<boolean>(true);

  // Fonction pour scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchMessages = async () => {
      let messages = await getMessages(conversationId, currentUser?._id);
      setMessages(messages);
    };
    fetchMessages();
  }, [conversationId, currentUser?._id]);

  // Scroll vers le bas seulement si il y a de nouveaux messages
  useEffect(() => {
    const currentMessagesLength = messages.length;
    const previousMessagesLength = previousMessagesLengthRef.current;

    // Détecter s'il y a de nouveaux messages
    if (
      previousMessagesLength > 0 &&
      currentMessagesLength > previousMessagesLength
    ) {
      setHasNewMessages(true);
    }

    // Scroll automatique seulement si :
    // 1. C'est le premier chargement (previousMessagesLength === 0)
    // 2. Il y a de nouveaux messages (currentMessagesLength > previousMessagesLength)
    // 3. L'utilisateur n'a pas désactivé le scroll automatique
    if (
      (previousMessagesLength === 0 ||
        currentMessagesLength > previousMessagesLength) &&
      shouldAutoScrollRef.current
    ) {
      scrollToBottom();
      setHasNewMessages(false); // Réinitialiser car on a scrollé automatiquement
    }

    // Mettre à jour la référence pour la prochaine comparaison
    previousMessagesLengthRef.current = currentMessagesLength;
  }, [messages]);

  // Rafraîchir les messages périodiquement pour les nouvelles réponses
  useEffect(() => {
    if (!conversationId) return;

    const interval = setInterval(async () => {
      try {
        const messages = await getMessages(conversationId, currentUser?._id);
        setMessages(messages);
      } catch (error) {
        console.error("Erreur lors du rafraîchissement des messages:", error);
      }
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, [conversationId, currentUser?._id]);

  const allMessages = messages.sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  const handleSendMessageClick = async () => {
    if (!text.trim()) return;
    const current = text;
    setText("");

    // Forcer le scroll automatique quand l'utilisateur envoie un message
    shouldAutoScrollRef.current = true;

    setMessages([
      ...messages,
      {
        id: `temp-${Date.now()}`,
        title: "Vous",
        position: "right",
        type: "text",
        text: current,
        date: new Date(),
        data: undefined,
      },
    ]);
    await sendTextMessage(conversationId, current);

    // Rafraîchir les messages après envoi pour s'assurer qu'ils sont bien sauvegardés
    setTimeout(async () => {
      try {
        const messages = await getMessages(conversationId, currentUser?._id);
        setMessages(messages);
      } catch (error) {
        console.error("Erreur lors du rafraîchissement après envoi:", error);
      }
    }, 1000);
  };

  const clearInput = () => {
    if (textInput.current) {
      textInput.current.value = "";
    }
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const handleOpenFileSubmission = () => {
    setIsFileSubmissionOpen(true);
  };

  const handleCloseFileSubmission = () => {
    setIsFileSubmissionOpen(false);
  };

  const handleFilesUploaded = async (files: File[]) => {
    try {
      // Forcer le scroll automatique quand l'utilisateur envoie des fichiers
      shouldAutoScrollRef.current = true;

      // Initialiser le statut d'upload
      setUploadStatus({
        loading: true,
        success: false,
        error: null,
        progress: 0,
      });

      let successCount = 0;
      let errorCount = 0;
      const totalFiles = files.length;

      // Envoyer chaque fichier individuellement en tant que binaire
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("files", file);

        try {
          const response = await fetch(
            `${BASE_URL}/conversations/${conversationId}/files/add`,
            {
              method: "POST",
              credentials: "include",
              body: formData,
            },
          );

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Erreur lors de l'upload du fichier ${file.name}`);
          }

          // Mettre à jour le progrès
          const progress = ((i + 1) / totalFiles) * 100;
          setUploadStatus((prev) => ({
            ...prev,
            progress,
          }));
        } catch (error) {
          errorCount++;
          console.error(
            `Erreur lors de l'upload du fichier ${file.name}:`,
            error,
          );
        }
      }

      // Définir le statut final
      if (errorCount === 0) {
        setUploadStatus({
          loading: false,
          success: true,
          error: null,
          progress: 100,
        });
        // Fermer le modal automatiquement en cas de succès complet
        setIsFileSubmissionOpen(false);

        // Rafraîchir les messages après upload réussi
        setTimeout(async () => {
          try {
            const messages = await getMessages(
              conversationId,
              currentUser?._id,
            );
            setMessages(messages);
          } catch (error) {
            console.error(
              "Erreur lors du rafraîchissement après upload:",
              error,
            );
          }
        }, 1000);
      } else if (successCount === 0) {
        setUploadStatus({
          loading: false,
          success: false,
          error: `Échec du téléchargement de tous les fichiers (${errorCount}/${totalFiles})`,
          progress: 100,
        });
      } else {
        setUploadStatus({
          loading: false,
          success: true,
          error: `Téléchargement partiel réussi (${successCount}/${totalFiles} fichiers)`,
          progress: 100,
        });
        // Fermer le modal automatiquement en cas de succès partiel
        setIsFileSubmissionOpen(false);

        // Rafraîchir les messages après upload partiel
        setTimeout(async () => {
          try {
            const messages = await getMessages(
              conversationId,
              currentUser?._id,
            );
            setMessages(messages);
          } catch (error) {
            console.error(
              "Erreur lors du rafraîchissement après upload partiel:",
              error,
            );
          }
        }, 1000);
      }

      // Réinitialiser le statut après 3 secondes
      setTimeout(() => {
        setUploadStatus({
          loading: false,
          success: false,
          error: null,
          progress: 0,
        });
      }, 3000);
    } catch (error) {
      console.error("Erreur lors du téléchargement des fichiers:", error);
      setUploadStatus({
        loading: false,
        success: false,
        error: "Erreur générale lors du téléchargement",
        progress: 0,
      });
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const textInput = React.useRef<HTMLInputElement>(null);

  return (
    <div
      onClick={handleClosePopup}
      className="flex flex-col h-full w-full min-h-0"
    >
      {/* Header fixe */}
      <div className="flex-shrink-0 flex flex-row bg-[#F1FBFF] space-x-2 p-4 border-b border-gray-200">
        <button
          className="text-black rounded px-1 py-1 flex items-center justify-center sm:hidden"
          onClick={onBack}
        >
          <ArrowLeftIcon className="w-5 h-5 stroke-2" />
        </button>
        {createTitle(`${conversationTitle}`)}
      </div>

      {/* Zone de contenu avec défilement */}
      <div className="flex-1 flex flex-col min-h-0">
        <div
          onClick={handleClosePopup}
          className="flex flex-col h-full w-full min-h-0"
        >
          {/* Zone des messages avec défilement */}
          <div
            className="flex-1 overflow-y-auto p-4 min-h-0 relative"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // Tolérance de 10px

              // Si l'utilisateur n'est pas en bas, désactiver le scroll automatique
              if (!isAtBottom) {
                shouldAutoScrollRef.current = false;
                setShowScrollButton(hasNewMessages); // Afficher le bouton si il y a de nouveaux messages
              } else {
                shouldAutoScrollRef.current = true;
                setShowScrollButton(false); // Masquer le bouton quand on est en bas
                setHasNewMessages(false); // Réinitialiser car on est en bas
              }
            }}
          >
            <div className="space-y-4">
              {allMessages.map((message, index) => {
                return createMessageBox(message, index);
              })}
              {/* Élément invisible pour le scroll automatique */}
              <div ref={messagesEndRef} />
            </div>

            {/* Bouton de scroll vers le bas */}
            {showScrollButton && (
              <button
                onClick={() => {
                  scrollToBottom();
                  setShowScrollButton(false);
                  setHasNewMessages(false);
                  shouldAutoScrollRef.current = true;
                }}
                className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                title="Aller aux nouveaux messages"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Zone de saisie fixe */}
          <div className="flex-shrink-0 bg-[#F1FBFF] p-4 border-t border-gray-200">
            {/* Feedback d'upload */}
            {(uploadStatus.loading ||
              uploadStatus.success ||
              uploadStatus.error) && (
              <div
                className={`mb-3 p-3 rounded-lg border ${
                  uploadStatus.loading
                    ? "bg-blue-50 border-blue-200 text-blue-800"
                    : uploadStatus.success
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  {uploadStatus.loading && (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Téléchargement en cours...</span>
                    </>
                  )}
                  {uploadStatus.success && !uploadStatus.loading && (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      <span>Téléchargement terminé !</span>
                    </>
                  )}
                  {uploadStatus.error && !uploadStatus.loading && (
                    <>
                      <XCircleIcon className="w-5 h-5 text-red-600" />
                      <span>{uploadStatus.error}</span>
                    </>
                  )}
                </div>

                {/* Barre de progression */}
                {uploadStatus.loading && (
                  <div className="mt-2">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadStatus.progress}%` }}
                      ></div>
                    </div>
                    <div className="text-xs mt-1 text-blue-600">
                      {Math.round(uploadStatus.progress)}% terminé
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isPopupOpen && (
              <Input
                className="bg-white rounded-lg"
                placeholder="Saisissez votre message"
                value={text}
                multiline={true}
                onChange={handleChangeText}
                referance={textInput}
                onSubmit={() => {
                  setText("");
                }}
                onKeyPress={() => {
                  setText("");
                }}
                leftButtons={
                  <button
                    className="bg-[#131CC9] text-white rounded px-1 py-1 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenFileSubmission();
                    }}
                  >
                    <PlusCircleIcon className="w-5 h-5 stroke-2" />
                  </button>
                }
                rightButtons={
                  <button
                    className="bg-[#131CC9] text-white rounded px-1 py-1 flex items-center justify-center"
                    onClick={(e) => {
                      handleSendMessageClick();
                      clearInput();
                    }}
                  >
                    <PaperAirplaneIcon className="w-5 h-5 stroke-2" />
                  </button>
                }
                maxHeight={60}
              />
            )}
          </div>
        </div>
      </div>

      {/* Composant de soumission de fichiers */}
      {isFileSubmissionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100">
            {/* Header inspiré de submissionDetails */}
            <div className="relative p-6 bg-[#E3B500] text-white flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#E3B500] font-bold">
                  <PaperClipIcon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Ajouter des fichiers</h2>
                  <p className="text-blue-100">
                    Sélectionnez les fichiers à envoyer
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseFileSubmission}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <PlusCircleIcon className="w-6 h-6 transform rotate-45" />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-hidden">
              <FileSubmission
                conversationId={conversationId}
                onSubmit={handleFilesUploaded}
              />
            </div>

            {/* Footer inspiré de submissionDetails */}
            <div className="p-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={handleCloseFileSubmission}
                className="px-4 py-2 bg-[#E3B500] text-white rounded-lg hover:bg-[#D4A500] transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
